import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { authClient } from '../../lib/auth-client'
import { CreditCard, Check, AlertCircle, ArrowRight } from 'lucide-react'

interface BillingInfo {
  status: 'active' | 'past_due' | 'canceled' | 'trialing'
  plan: string
  currentPeriodEnd: string
  paymentMethod: {
    brand: string
    last4: string
  } | null
}

export function BillingPage() {
  const { data: session } = authClient.useSession()
  const [billing, setBilling] = useState<BillingInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)

  useEffect(() => {
    fetchBillingInfo()
  }, [])

  const fetchBillingInfo = async () => {
    try {
      const res = await fetch('/api/v1/billing', {
        headers: { 'Authorization': `Bearer ${session?.session.token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setBilling(data)
      }
    } catch (err) {
      console.error('Failed to fetch billing:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const upgradePlan = async () => {
    setIsCheckoutLoading(true)
    try {
      const res = await fetch('/api/v1/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.session.token}`
        }
      })

      if (res.ok) {
        const { url } = await res.json()
        window.location.href = url
      }
    } catch (err) {
      console.error('Failed to start checkout:', err)
    } finally {
      setIsCheckoutLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Billing | Twenty</title>
      </Helmet>

      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Billing</h1>
          <p className="text-muted-foreground">
            Manage your subscription and payment methods
          </p>
        </div>

        {/* Current Plan */}
        <div className="card-factory p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-medium">Current Plan</h2>
              {isLoading ? (
                <p className="text-sm text-muted-foreground" aria-live="polite">Loading…</p>
                ) : billing ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {billing.plan} - {billing.status}
                    </p>
                    {billing.currentPeriodEnd && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Renews {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(billing.currentPeriodEnd))}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Free Plan</p>
                )}
              </div>
            </div>
            {billing?.status === 'past_due' && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-sm text-sm">
                <AlertCircle className="w-4 h-4" />
                Payment required
              </div>
            )}
          </div>
        </div>

        {/* Payment Method */}
        {billing?.paymentMethod && (
          <div className="card-factory p-6">
            <h2 className="text-lg font-medium mb-4">Payment Method</h2>
            <div className="flex items-center gap-3">
              <div className="w-10 h-6 bg-secondary rounded-sm flex items-center justify-center text-xs font-medium">
                {billing.paymentMethod.brand.toUpperCase()}
              </div>
              <span className="text-sm">•••• {billing.paymentMethod.last4}</span>
            </div>
          </div>
        )}

        {/* Pricing */}
        <div>
          <h2 className="text-xl font-medium mb-4">Your Plan</h2>
          <div className="card-factory p-6 border-primary">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold">Pro</h3>
                <p className="text-sm text-muted-foreground">$99/mo</p>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-xs font-medium rounded-sm">
                Current Plan
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {[
                'Fair use policy',
                'All services included',
                'No hard limits',
                'Priority support',
                'Auto-scaling',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary" />
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={upgradePlan}
              disabled={isCheckoutLoading}
              className="btn-factory w-full"
            >
              {isCheckoutLoading ? 'Loading…' : 'Manage subscription'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* FAQ */}
        <div className="p-4 bg-secondary/50 rounded-sm">
          <h3 className="font-medium mb-2">Billing Questions?</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Contact us at billing@twenty.sh for any billing inquiries.
          </p>
          <Link to="/refund" className="text-sm text-primary hover:underline">
            View refund policy
          </Link>
        </div>
      </div>
    </>
  )
}
